import type { Interview } from "@/types";
import { InterviewCard } from "../card/InterviewCard";

const START_H = 0;
const END_H = 24;
const HOUR_PX = 64;
const HOURS = Array.from({ length: END_H - START_H }, (_, i) => START_H + i);

interface Props {
  date: Date;
  interviews: Interview[];
  onInterviewClick: (iv: Interview) => void;
  onSlotClick: (date: Date) => void;
  onInterviewDrop: (id: number, newDate: Date) => void;
}

function fmtHour(h: number) {
  return new Date(0, 0, 0, h).toLocaleTimeString(undefined, { hour: "numeric" });
}

function positionOf(iv: Interview) {
  const d = new Date(iv.scheduled_at);
  const top = (d.getHours() - START_H + d.getMinutes() / 60) * HOUR_PX;
  const height = Math.max(22, (iv.duration_minutes / 60) * HOUR_PX - 2);
  return { top, height };
}

export function DayView({ date, interviews, onInterviewClick, onSlotClick, onInterviewDrop }: Props) {
  const dayStart = new Date(date); dayStart.setHours(START_H, 0, 0, 0);
  const dayEnd = new Date(date); dayEnd.setHours(END_H, 0, 0, 0);

  const visible = interviews.filter((iv) => {
    const t = new Date(iv.scheduled_at).getTime();
    return t >= dayStart.getTime() && t < dayEnd.getTime();
  });

  const totalH = (END_H - START_H) * HOUR_PX;

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const id = Number(e.dataTransfer.getData("interview-id"));
    if (!id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const rawH = (e.clientY - rect.top) / HOUR_PX + START_H;
    const snappedMin = Math.round((rawH % 1) * 4) * 15;
    const d = new Date(date);
    d.setHours(Math.floor(rawH), snappedMin, 0, 0);
    onInterviewDrop(id, d);
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Sticky day header */}
      <div className="flex shrink-0 shadow-sm">
        <div className="w-14 shrink-0" />
        <div className="flex-1 flex h-8 items-center justify-center border-l border-border-card">
          <span className="text-sm font-semibold text-near-black">
            {date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
          </span>
        </div>
      </div>

      {/* Scrollable time grid */}
      <div className="flex flex-1 overflow-auto min-h-0">
        {/* Time labels */}
        <div className="w-14 shrink-0">
          {HOURS.map((h) => (
            <div key={h} style={{ height: HOUR_PX }} className="relative">
              <span className="absolute -top-2 right-2 text-[10px] text-text-secondary">{fmtHour(h)}</span>
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div
          className="relative flex-1 border-l border-border-card"
          style={{ height: totalH }}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
          onDrop={handleDrop}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const rawH = (e.clientY - rect.top) / HOUR_PX + START_H;
            const d = new Date(date);
            d.setHours(Math.floor(rawH), Math.round((rawH % 1) * 4) * 15, 0, 0);
            onSlotClick(d);
          }}
        >
          {HOURS.map((h) => (
            <div key={h} style={{ top: (h - START_H) * HOUR_PX, height: HOUR_PX }}
              className="absolute w-full border-b border-border-card/50" />
          ))}
          {visible.map((iv) => {
            const { top, height } = positionOf(iv);
            return (
              <InterviewCard
                key={iv.id}
                interview={iv}
                style={{ position: "absolute", top, left: 4, right: 4, height }}
                onClick={(e) => { e.stopPropagation(); onInterviewClick(iv); }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
